// Whole-session PDF export target. Mounted once in the reporting top nav so
// it's always in the tree; renders nothing until the user picks "Entire
// session (pdf)" from the Export dropdown. When the Redux flag flips on:
//
// 1. All three session-wide data hooks fire (summary, per-player stats,
//    roster). Reporting uses RTK Query, so if the current view already
//    mounted these, the cache resolves synchronously.
// 2. DashboardPrint renders hidden on-screen + visible to print.
// 3. useLayoutEffect waits for data to arrive, then calls window.print().
// 4. `onafterprint` (+ a fallback timeout) clears the flag.
//
// SCOPE NOTE: "Entire session (pdf)" in the Figma handoff implies all three
// view-level print layouts stitched together. This first iteration renders
// the dashboard print layout only — it covers the session-wide stats the
// designer specifically called out (summary tiles, performance chart, top
// performers). Team-stats and player-detail print layouts depend on UI
// state (active tab, selected player) that has no sensible default for a
// "whole session" context. Follow-up work may extend this to stitch the
// team-stats print once we decide which default tab to use.
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import {
  useAllPlayersSessionSummary,
  useAppDispatch,
  useAppSelector,
  useSessionPlayers,
  useTeamSessionSummary
} from '@frontend/react'
import {
  finishWholeSessionPrint,
  selectReportingPrintingWholeSession
} from '@frontend/core/lib/domains/reporting'
import { useReportingState } from '../lib/reporting-state'
import { buildDashboardCards } from '../views/dashboard/formatter'
import { DashboardPrint } from '../views/dashboard/index.print'

export function SessionWholeExport() {
  const isPrinting = useAppSelector(selectReportingPrintingWholeSession)

  if (!isPrinting) return null
  return <SessionWholeExportInner />
}

function SessionWholeExportInner() {
  const dispatch = useAppDispatch()
  const { teamId, sessionId, config } = useReportingState()
  const { list: players, isLoading: playersLoading } = useSessionPlayers(
    teamId,
    sessionId
  )
  const { data: sessionSummary, isLoading: summaryLoading } =
    useTeamSessionSummary(teamId, sessionId)
  const { playerStats, isLoading: statsLoading } = useAllPlayersSessionSummary(
    teamId,
    sessionId
  )
  const cards = useMemo(
    () => buildDashboardCards(sessionSummary, playerStats, players, config),
    [sessionSummary, playerStats, players, config]
  )

  const isLoading = playersLoading || summaryLoading || statsLoading
  const printedRef = useRef(false)

  useLayoutEffect(() => {
    if (printedRef.current || isLoading) return
    printedRef.current = true
    // Defer to the next tick so React has committed the print layout
    // before the browser captures the document for printing.
    const raf = requestAnimationFrame(() => window.print())
    return () => cancelAnimationFrame(raf)
  }, [isLoading])

  useEffect(() => {
    const cleanup = () => dispatch(finishWholeSessionPrint())
    window.addEventListener('afterprint', cleanup, { once: true })
    // Some browsers (older Safari) don't fire `afterprint` reliably.
    // A 30s fallback ensures the flag clears even if the event is missed.
    const timeout = window.setTimeout(cleanup, 30_000)
    return () => {
      window.removeEventListener('afterprint', cleanup)
      window.clearTimeout(timeout)
    }
  }, [dispatch])

  return <DashboardPrint summary={sessionSummary} cards={cards} />
}
